import { FlexBox, Text } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import {
  PartnerIntegrations,
  ShowNotificationFunctionProps,
} from 'lib-integrations-partner-widgets';
import { useHasAccess } from 'lib-shared-access-control';
import { config } from 'lib-shared-backend-config';
import {
  MetaConversation,
  NotificationPayloadT,
} from 'lib-shared-legacy-types';
import { useCallback, useLayoutEffect } from 'react';

import { AddContactAttributesModal } from 'components/contact';
import { SidebarToggleTrigger } from 'components/conversation/components/sidebar/SidebarToggleTrigger';
import { notificationActions } from 'components/notifications/actions/notification';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useBreakpointValue } from 'utils/mediaQuery';
import { usePartnerWidgetFeatureFlags } from 'utils/partnerIntegrationsWidget/usePartnerWidgetFeatureFlags';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import ContactCard from '../../components/sidebar/cards/ContactCard';
import FeedbackCard from '../../containers/sidebar/cards/feedbackCard';

import { FeedbackT } from './cards/FeedbackCard';
import { SuggestionCards } from './cards/KnowledgeCard';
import { CustomCards } from './cards/customCards/customCards';
import { ConversationCustomFieldsCard } from './cards/customFields';
import { HistoryCard } from './cards/historyCard/HistoryCard';
import { UserTrackingCard } from './cards/userTrackingCard';
import { partnerIntegrationNotificationTimeout } from './partnerIntegrations';

type CustomCardId = string;
type CardIsOpen = boolean;
export type ConversationCardsSettings = Record<CustomCardId, CardIsOpen>;

type PropsT = {
  metaConversation: MetaConversation;
  conversationCardsSettings: ConversationCardsSettings;
  isMenuOpen: boolean;
  feedback?: FeedbackT[];
  toggleCustomCardMenu: (arg: {
    key: string;
    value: boolean;
  }) => Promise<unknown>;
  updateConversationCards: (arg: {
    key: string;
    value: boolean;
  }) => Promise<unknown>;
  hasCollapseTrackingCard: boolean;
  showTrackingCard: boolean;
};

const ConversationSidebar = ({
  metaConversation,
  conversationCardsSettings,
  updateConversationCards,
  feedback,
  isMenuOpen,
  toggleCustomCardMenu,
  hasCollapseTrackingCard,
  showTrackingCard,
}: PropsT): JSX.Element => {
  const dispatch = useAppDispatch();
  const isSmallLayout = useBreakpointValue({
    base: true,
    lg: false,
  });

  const { partnerIntegrationsWidget } = useFeatureFlag([
    'partner-integrations-widget',
  ]);

  const hasCustomAttributeAccess = useHasAccess(
    'conversation.custom_attribute',
  );

  const hasIntegrationAccess = useHasAccess('conversation.integration');

  const hasSatisfactionAccess = useHasAccess('conversation.satisfaction');

  const partnerWidgetFeatureFlags = usePartnerWidgetFeatureFlags();

  const isCardOpen = (key: string): boolean => {
    return conversationCardsSettings[key] ?? false;
  };

  const toggleCard = (key: string) => {
    updateConversationCards({ key, value: !isCardOpen(key) });
  };

  const toggleSidebar = () => {
    toggleCustomCardMenu({
      key: 'isCustomCardMenuOpen',
      value: !isMenuOpen,
    });
  };

  useLayoutEffect(() => {
    if (isSmallLayout) {
      toggleCustomCardMenu({
        key: 'isCustomCardMenuOpen',
        value: false,
      });
    }
  }, [isSmallLayout, toggleCustomCardMenu]);

  const showNotification = useCallback(
    ({ title, text, icon, severity }: ShowNotificationFunctionProps) => {
      const time =
        severity === 'warning' || severity === 'danger'
          ? 0
          : partnerIntegrationNotificationTimeout;
      const className = severity as string;

      const payload: NotificationPayloadT = {
        title,
        text,
        icon,
        className,
        time,
      };
      dispatch(notificationActions.notificationAdd(payload));
    },
    // We only pass this into PartnerIntegrations component as a callback
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const rootClassName = classnames('conversation-sidebar-holder', {
    closed: !isMenuOpen,
  });

  return (
    <div className={rootClassName}>
      <SidebarToggleTrigger
        isMenuOpen={isMenuOpen}
        toggleSidebar={toggleSidebar}
      />

      <ContactCard
        onToggle={() => toggleCard('contact')}
        isOpen={isCardOpen('contact')}
        isMinimized={!isMenuOpen}
      />

      {hasCustomAttributeAccess && metaConversation.conversation.id && (
        <ConversationCustomFieldsCard
          csid={metaConversation.conversation.id}
          isMinimized={!isMenuOpen}
          isOpen={isCardOpen('custom-fields')}
          onToggle={() => toggleCard('custom-fields')}
        />
      )}

      {metaConversation.requester &&
        metaConversation.previousConversations &&
        metaConversation.previousConversations?.length !== 0 && (
          <HistoryCard
            onToggle={() => toggleCard('history')}
            isOpen={isCardOpen('history')}
            previousConversations={metaConversation.previousConversations}
            requester={metaConversation.requester}
            isMinimized={!isMenuOpen}
          />
        )}

      {/** SuggestionCards are conditionally rendered */}
      <SuggestionCards isMinimized={!isMenuOpen} />

      {hasIntegrationAccess && (
        <>
          {isMenuOpen && (
            <FlexBox alignItems="center" padding="300">
              <FlexBox padding="200 0">
                <Text.Paragraph variant="3" subdued>
                  Integrations
                </Text.Paragraph>
              </FlexBox>
            </FlexBox>
          )}

          {partnerIntegrationsWidget && (
            <PartnerIntegrations
              configuration={{
                baseUrl: config.url.partnerIntegrations,
                featureFlags: partnerWidgetFeatureFlags,
              }}
              conversationContext={{
                conversationId: metaConversation.conversation?.id || null,
                agent:
                  metaConversation?.conversation?.assignment?.agent ??
                  undefined,
                customer: metaConversation?.requester ?? undefined,
              }}
              showNotification={showNotification}
              onToggleFn={toggleCard}
              isOpenFn={isCardOpen}
              isMinimized={!isMenuOpen}
            />
          )}

          <CustomCards
            isMinimized={!isMenuOpen}
            csid={metaConversation.conversation?.id}
          />
        </>
      )}

      {hasSatisfactionAccess && metaConversation.conversation.id && (
        <FeedbackCard
          onToggle={() => toggleCard('feedback')}
          isOpen={isCardOpen('feedback')}
          feedback={feedback}
          csid={metaConversation.conversation.id as any}
          isMinimized={!isMenuOpen}
        />
      )}

      {showTrackingCard && (
        <UserTrackingCard
          onToggle={() => toggleCard('tracking')}
          isOpen={isCardOpen('tracking')}
          attributes={metaConversation.conversation.attributes ?? {}}
          isMinimized={!isMenuOpen}
          hasCollapseTrackingCard={hasCollapseTrackingCard}
        />
      )}

      <AddContactAttributesModal />
    </div>
  );
};

export default ConversationSidebar;
